import React, { useState, useEffect, useCallback, useRef } from 'react';

const FreeGiftModal = ({ shopifyData = {} }) => {
  const thresholds = shopifyData.thresholds || [];
  const stackFreeGifts = shopifyData.stackFreeGifts !== false;
  const modalHeading = shopifyData.modalHeading;
  const modalSubtitle = shopifyData.modalSubtitle;
  const modalCta = shopifyData.modalCta; 
  const STORAGE_KEY_MANUALLY_CLOSED = 'free_gift_modal_manually_closed';
  const STORAGE_KEY_PREVIOUS_ACTIVE = 'free_gift_modal_previous_active';
  const STORAGE_KEY_ACTIVE_GIFTS = 'free_gift_modal_active_gifts';

  // Initialize previousHasActiveGift from localStorage
  const getPreviousActiveGiftFromStorage = () => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY_PREVIOUS_ACTIVE);
      return stored === 'true';
    } catch (e) {
      return false;
    }
  };

  const getCartSubtotal = useCallback((cart) => {
    let cartSubtotal = cart.items_subtotal_price || 0;
    if(!cartSubtotal) { 
      return false
    }
    cartSubtotal = cartSubtotal / 100;
    return cartSubtotal;
  }, []);

  // Get stored active gifts from localStorage
  const getStoredActiveGifts = useCallback(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY_ACTIVE_GIFTS);
      if (stored) {
        return JSON.parse(stored);
      }
      return [];
    } catch (e) {
      console.error('Error reading stored active gifts:', e);
      return [];
    }
  }, []);

  // Store active gifts in localStorage
  const setStoredActiveGifts = useCallback((gifts) => {
    try {
      const variantIds = gifts.map(gift => gift.variant?.id).filter(Boolean);
      localStorage.setItem(STORAGE_KEY_ACTIVE_GIFTS, JSON.stringify(variantIds));
    } catch (e) {
      console.error('Error storing active gifts:', e);
    }
  }, []);

  // Remove active gifts from localStorage when cart drops below threshold
  const syncActiveGiftsWithCart = useCallback((cart) => {
    if (!cart || !thresholds.length) return;
    const cartSubtotal = getCartSubtotal(cart);
    if(!cartSubtotal) { 
      return;
    }
    const storedGiftIds = getStoredActiveGifts();
    
    if (storedGiftIds.length === 0) return;

    // Filter out gifts that are no longer eligible (cart value dropped below threshold)
    const stillEligibleIds = storedGiftIds.filter((variantId) => {
      // Find the threshold that matches this variant ID
      const threshold = thresholds.find(t => t.product?.id === variantId);
      if (!threshold) {
        // If threshold not found, remove from storage (threshold might have been removed)
        return false;
      }
      // Keep if cart value is still above threshold
      return cartSubtotal >= threshold.threshold;
    });

    // Update localStorage if any gifts were removed
    if (stillEligibleIds.length !== storedGiftIds.length) {
      localStorage.setItem(STORAGE_KEY_ACTIVE_GIFTS, JSON.stringify(stillEligibleIds));
    }
  }, [thresholds, getStoredActiveGifts]);

  const [cartData, setCartData] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [previousHasActiveGift, setPreviousHasActiveGift] = useState(getPreviousActiveGiftFromStorage);
  const modalRef = useRef(null);

  // Fetch cart data
  const fetchCartData = useCallback(async () => {
    try {
      const response = await fetch('/cart.js');
      const data = await response.json();
      setCartData(data);
      return data;
    } catch (error) {
      console.error('Error fetching cart data:', error);
      return null;
    }
  }, []);

  // Encode threshold and variantId into a single alphanumeric string
  const encodeThresholdAndVariant = useCallback((threshold, variantId) => {
    // Encode threshold to base36 (0-9, a-z)
    const thresholdEncoded = parseInt(threshold.toString()).toString(36);
    
    // Encode variantId to base36
    const variantEncoded = parseInt(variantId.toString()).toString(36);
    
    // Combine with a delimiter that's also alphanumeric (use 'x' as separator)
    // Format: thresholdEncoded x variantEncoded
    return `${thresholdEncoded}x${variantEncoded}`;
  }, []);

  // Decode alphanumeric string back to threshold and variantId
  const decodeThresholdAndVariant = useCallback((encoded) => {
    if (!encoded) return null;
    
    try {
      const [thresholdEncoded, variantEncoded] = encoded.split('x');
      
      // Decode from base36
      const threshold = parseInt(thresholdEncoded, 36);
      const variantId = parseInt(variantEncoded, 36);
      
      return { threshold, variantId };
    } catch (e) {
      console.error('Error decoding threshold and variant:', e);
      return null;
    }
  }, []);

  // Get the single highest eligible gift (used when stackFreeGifts is false)
  const getHighestEligibleGift = useCallback((cart) => {
    if (!cart || !thresholds.length) return null;
    const cartSubtotal = getCartSubtotal(cart);
    if (!cartSubtotal) return null;

    const sorted = [...thresholds].sort((a, b) => b.threshold - a.threshold);
    const highest = sorted.find(t => cartSubtotal >= t.threshold && t.product);
    if (!highest) return null;

    const variantId = highest.product.id;
    const isInCart = cart.items?.some(item =>
      item.variant_id === variantId && item.properties?._gift_id
    );
    if (isInCart) return null;

    return {
      variant: highest.product,
      threshold: highest.threshold,
      productTitle: highest.product?.title,
      productImage: highest.product?.image,
    };
  }, [thresholds, getCartSubtotal]);

  // Get available gifts based on cart data
  const getAvailableGifts = useCallback((cart) => {
    if (!cart || !thresholds.length) return [];

    if (!stackFreeGifts) {
      const gift = getHighestEligibleGift(cart);
      return gift ? [gift] : [];
    }

    let cartSubtotal = getCartSubtotal(cart);
    if(!cartSubtotal) {
      return [];
    }
    const availableGifts = [];

    thresholds.forEach((threshold) => {
      if (cartSubtotal >= threshold.threshold && threshold.product) {
        const variantId = threshold.product.id;
        const isInCart = cart.items?.some((item) => {
          if (item.variant_id === variantId) {
            // Check for _gift_id property
            return item.properties?._gift_id
          }
          return false;
        });

        if (!isInCart) {
          availableGifts.push({
            variant: threshold.product,
            threshold: threshold.threshold,
            productTitle: threshold.product?.title,
            productImage: threshold.product?.image
          });
        }
      }
    });

    return availableGifts;
  }, [thresholds, stackFreeGifts, getHighestEligibleGift]);

  // Check if has active gift
  const hasActiveGift = useCallback((cart) => {
    if (!cart || !thresholds.length) return false;
    const cartSubtotal = getCartSubtotal(cart);
    if(!cartSubtotal) { 
      return false
    }
    return thresholds.some((threshold) => cartSubtotal >= threshold.threshold);
  }, [thresholds, getCartSubtotal]);

  // Check if modal was manually closed (from localStorage)
  const isManuallyClosed = useCallback(() => {
    try {
      return localStorage.getItem(STORAGE_KEY_MANUALLY_CLOSED) === 'true';
    } catch (e) {
      return false;
    }
  }, []);

  // Set manually closed flag in localStorage
  const setManuallyClosed = useCallback(() => {
    try {
      localStorage.setItem(STORAGE_KEY_MANUALLY_CLOSED, 'true');
    } catch (e) {
      console.error('Error setting localStorage:', e);
    }
  }, []);

  // Clear manually closed flag from localStorage
  const clearManuallyClosed = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY_MANUALLY_CLOSED);
    } catch (e) {
      console.error('Error clearing localStorage:', e);
    }
  }, []);

  // Store previous active gift status in localStorage
  const setPreviousActiveGiftInStorage = useCallback((value) => {
    try {
      localStorage.setItem(STORAGE_KEY_PREVIOUS_ACTIVE, value ? 'true' : 'false');
    } catch (e) {
      console.error('Error setting previous active gift in localStorage:', e);
    }
  }, []);

  // Check if there are new gifts (compares with stored active gifts in localStorage)
  const hasNewGifts = useCallback((availableGifts) => {
    // If not manually closed, always allow showing
    if (!isManuallyClosed()) return true;

    // Get stored active gifts from localStorage
    const storedGiftIds = getStoredActiveGifts();
    
    // If no stored gifts, consider all as new
    if (storedGiftIds.length === 0) {
      return availableGifts.length > 0;
    }

    // Check if any current gift is not in stored gifts (new gift unlocked)
    const storedIdsSet = new Set(storedGiftIds);
    const hasNew = availableGifts.some((gift) => {
      const variantId = gift.variant?.id;
      return variantId && !storedIdsSet.has(variantId);
    });

    return hasNew;
  }, [isManuallyClosed, getStoredActiveGifts]);

  // Store previous available gifts to detect new ones
  const [previousAvailableGifts, setPreviousAvailableGifts] = useState([]);

  // Helper function to close modal and clear overflow
  const closeModal = useCallback(() => {
    setIsOpen(false);
    
    // Immediately clear overflow to prevent body scroll lock
    document.body.style.overflow = '';
    
    // Also remove is-open class from modal element if it exists
    const modalElement = modalRef.current || document.querySelector('.precision-report-modal-overlay[data-modal-id="free-gift-modal"]');
    if (modalElement) {
      modalElement.classList.remove('is-open');
    }
  }, []);

  // Handle cart update with cart data
  const handleCartUpdateWithData = useCallback((cart) => {
    if (!cart) return;

    // Update cart data state
    setCartData(cart);

    const activeGift = hasActiveGift(cart);
    const availableGifts = getAvailableGifts(cart);

    // Initialize previous available gifts on first load
    if (previousAvailableGifts.length === 0 && availableGifts.length > 0) {
      setPreviousAvailableGifts(availableGifts);
    }

    // Sync active gifts with cart - remove gifts that are no longer eligible
    syncActiveGiftsWithCart(cart);

    // Reset localStorage flag if has_active_gift goes to false
    if (!activeGift && previousHasActiveGift) {
      clearManuallyClosed();
      setStoredActiveGifts([]);
    }
    
    // Update previous active gift status in state and localStorage
    if (activeGift !== previousHasActiveGift) {
      setPreviousHasActiveGift(activeGift);
      setPreviousActiveGiftInStorage(activeGift);
    }

    // Check if there are new gifts (new gifts enable the modal again)
    const hasNew = hasNewGifts(availableGifts);
    
    // If new gifts are available, clear the manually closed flag and update stored gifts
    if (hasNew && availableGifts.length > 0) {
      clearManuallyClosed();
      // Update stored active gifts with all currently available gifts
      setStoredActiveGifts(availableGifts);
    } else if (availableGifts.length > 0) {
      // Even if no new gifts, update stored gifts to include all current eligible gifts
      setStoredActiveGifts(availableGifts);
    }

    // Only show modal if:
    // 1. Gift is active
    // 2. There are available gifts
    // 3. Modal is not already open
    // 4. Modal was not manually closed (or new gifts are available)
    if (activeGift && availableGifts.length > 0 && !isOpen) {
      if (!isManuallyClosed() || hasNew) {
        setIsOpen(true);
      }
    } else if (availableGifts.length === 0 && isOpen) {
      closeModal();
    }

    // Update previous available gifts (for backward compatibility)
    setPreviousAvailableGifts(availableGifts);
  }, [hasActiveGift, getAvailableGifts, hasNewGifts, isOpen, previousHasActiveGift, previousAvailableGifts, isManuallyClosed, clearManuallyClosed, syncActiveGiftsWithCart, setStoredActiveGifts, closeModal]);

  // Handle cart update
  const handleCartUpdate = useCallback((event) => {
    // Get cart data from event detail
    const cart = event?.detail?.cartData;
    if (!cart) return;
    
    handleCartUpdateWithData(cart);
  }, [handleCartUpdateWithData]);

  // Initial cart fetch on component mount
  // useEffect(() => {
  //   fetchCartData().then((cart) => {
  //     if (cart) {
  //       handleCartUpdateWithData(cart);
  //     }
  //   });
  // }, [handleCartUpdate]);

  // Subscribe to cart update events
  useEffect(() => {
    // Listen for custom cart-data-updated event with cart data
    document.addEventListener('cart-data-updated', handleCartUpdate);

    return () => {
      document.removeEventListener('cart-data-updated', handleCartUpdate);
    };
  }, [handleCartUpdate]);

  // Handle manual close
  const handleManualClose = useCallback(() => {
    // Set flag in localStorage
    setManuallyClosed();
    closeModal();
  }, [setManuallyClosed, closeModal]);

  // Handle open modal - exposed on window
  const handleOpenModal = useCallback(() => {
    // Use existing cart data from state
    if (!cartData) return;

    const availableGifts = getAvailableGifts(cartData);
    const activeGift = hasActiveGift(cartData);

    // Only open if there's an active gift and available gifts
    if (activeGift && availableGifts.length > 0) {
      // Clear manually closed flag so modal can render
      clearManuallyClosed();
      
      // Wait for next render cycle to ensure modal element exists
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsOpen(true);
        });
      });
    }
  }, [cartData, getAvailableGifts, hasActiveGift, clearManuallyClosed]);

  // Handle add to cart
  const handleAddToCart = useCallback(async () => {
    if (isLoading || !cartData) return;

    const availableGifts = getAvailableGifts(cartData);
    if (availableGifts.length === 0) return;

    setIsLoading(true);

    try {
      // When not stacking, remove any lower-tier gift items before adding the highest
      if (!stackFreeGifts && availableGifts.length > 0) {
        const highestThreshold = availableGifts[0].threshold;
        const lowerTierLines = cartData.items
          ?.map((item, i) => ({ item, line: i + 1 }))
          .filter(({ item }) => {
            if (!item.properties?._gift_id) return false;
            const decoded = decodeThresholdAndVariant(item.properties._gift_id);
            return decoded && decoded.threshold < highestThreshold;
          }) ?? [];

        for (const { line } of lowerTierLines) {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ line, quantity: 0 }),
          });
        }
      }

      const items = availableGifts.map((gift) => ({
        id: gift.variant.id,
        quantity: 1,
        threshold: gift.threshold,
        properties: {}
      }));

      const protocolCartSummary = document.querySelector('protocol-cart-summary');
      const cartDrawer = document.querySelector('cart-drawer');

      let sections;
      if (protocolCartSummary) {
        const sectionId = protocolCartSummary.dataset?.sectionId;
        sections = sectionId || '';
      } else {
        sections = cartDrawer && typeof cartDrawer.getSectionsToRender === 'function'
          ? cartDrawer.getSectionsToRender().map((section) => section.id).join(',')
          : 'cart-drawer,cart-icon-bubble';
      }

      const formData = new FormData();
      items.forEach((item, index) => {
        formData.append(`items[${index}][id]`, item.id);
        formData.append(`items[${index}][quantity]`, item.quantity);
        // Encode and send threshold and variantId together
        if (item.threshold && item.id) {
          const encoded = encodeThresholdAndVariant(item.threshold, item.id);
          if (encoded) {
            formData.append(`items[${index}][properties][_gift_id]`, encoded);

          }
        }
      });
      formData.append('sections', sections);
      formData.append('sections_url', window.location.pathname);

      const response = await fetch(routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Accept': 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to add to cart');
      }

      const data = await response.json();

      if (data.status) {
        throw new Error(data.message || data.description || 'Failed to add to cart');
      }

      // Update cart — protocol cart page or drawer
      if (protocolCartSummary && typeof protocolCartSummary.refresh === 'function') {
        protocolCartSummary.refresh();
      } else if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
        cartDrawer.renderContents(data);
      }

      // Close modal and clear overflow
      closeModal();

    } catch (error) {
      console.error('Error adding free gift to cart:', error);
      alert(error.message || 'Failed to add free gift to cart. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, cartData, getAvailableGifts, encodeThresholdAndVariant, stackFreeGifts, decodeThresholdAndVariant]);

  // Sync isOpen state with modal class and body overflow
  useEffect(() => {
    const modalElement = modalRef.current || document.querySelector('.precision-report-modal-overlay[data-modal-id="free-gift-modal"]');
    
    if (isOpen) {
      if (modalElement) {
        modalElement.classList.add('is-open');
      }
      document.body.style.overflow = 'hidden';
    } else {
      if (modalElement) {
        modalElement.classList.remove('is-open');
      }
      // Always clear overflow when closing
      document.body.style.overflow = '';
    }

    // Cleanup: always clear overflow when component unmounts or isOpen changes
    return () => {
      if (modalElement) {
        modalElement.classList.remove('is-open');
      }
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;
    
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        handleManualClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, handleManualClose]);

  // Expose openFreeGiftModal function on window
  useEffect(() => {
    window.openFreeGiftModal = handleOpenModal;
    
    return () => {
      if (window.openFreeGiftModal === handleOpenModal) {
        delete window.openFreeGiftModal;
      }
    };
  }, [handleOpenModal]);

  if (!cartData) {
    return null;
  }

  const availableGifts = getAvailableGifts(cartData);
  const activeGift = hasActiveGift(cartData);

  // Don't render if no active gift or no available gifts
  if (!activeGift || availableGifts.length === 0) {
    return null;
  }

  // Don't render if manually closed and no new gifts
  if (isManuallyClosed()) {
    const hasNew = hasNewGifts(availableGifts);
    if (!hasNew) {
      return null;
    }
  }
  

  return (
    <div 
      ref={modalRef}
      className={`precision-report-modal-overlay ${isOpen ? 'is-open' : ''}`} 
      data-modal-id="free-gift-modal"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          handleManualClose();
        }
      }}
    >
      <div className="tw-bg-background-white tw-content-stretch tw-flex tw-flex-col tw-gap-[8px] tw-items-start tw-p-[32px] tw-relative tw-rounded-[16px] tw-shadow-md tw-max-w-[500px] tw-w-full tw-mx-auto">
        {/* Close Button */}
        <button
          type="button"
          data-modal-close
          className="tw-absolute tw-bg-background tw-content-stretch tw-flex tw-items-center tw-justify-center tw-right-[16px] tw-rounded-[16px] tw-w-[32px] tw-h-[32px] tw-top-[16px] tw-cursor-pointer tw-border-0"
          aria-label="Close modal"
          onClick={handleManualClose}
          style={{
            zIndex: 1000,
            padding: '15px',
            cursor: 'pointer',
            background: '#F7F7F7',
          }}
        >
          <div className="tw-flex tw-flex-col tw-font-sans tw-font-normal tw-justify-center tw-leading-tight tw-relative tw-shrink-0 tw-text-text-dark tw-text-[18px] tw-text-center tw-whitespace-nowrap">
            <p className="tw-leading-[27px] tw-m-0">×</p>
          </div>
        </button>

        {/* Heading */}
        <div className="tw-content-stretch tw-flex tw-flex-col tw-items-center tw-relative tw-shrink-0 tw-w-full">
          <div className="tw-flex tw-flex-col tw-font-sans tw-font-extrabold tw-justify-center tw-leading-tight tw-relative tw-shrink-0 tw-text-text tw-text-[24px] tw-text-center tw-whitespace-nowrap">
            <p className="tw-leading-[36px] tw-m-0">{modalHeading}</p>
          </div>
        </div>

        {/* Subtitle */}
        <div className="tw-content-stretch tw-flex tw-flex-col tw-items-center tw-relative tw-shrink-0 tw-w-full">
          <div className="tw-flex tw-flex-col tw-font-sans tw-font-normal tw-justify-center tw-leading-tight tw-relative tw-shrink-0 tw-text-[#64748b] tw-text-[16px] tw-text-center tw-whitespace-nowrap">
            <p className="tw-leading-[24px] tw-m-0">{modalSubtitle}</p>
          </div>
        </div>

        {/* Product List Container */}
        <div className="tw-content-stretch tw-flex tw-flex-col tw-items-start tw-py-[16px] tw-relative tw-shrink-0 tw-w-full tw-gap-[16px] ">
          {availableGifts.map((gift, index) => {
            const variant = gift.variant;
            const variantId = variant?.id;
            const productTitle = gift.productTitle || 'Free Gift';
            const productImage = gift.productImage || '';
            const comparePrice = variant?.price || 0;
            const currentPrice = 0;

            return (
              <div
                key={variantId || index}
                className="tw-border tw-border-gray-light tw-border-solid tw-content-stretch tw-flex tw-gap-[16px] tw-items-center tw-p-[17px] tw-relative tw-rounded-[6px] tw-shrink-0 tw-w-full"
                data-variant-id={variantId}
              >
                {/* Product Image */}
                <div className="tw-max-w-[287px] tw-relative tw-rounded-[6px] tw-shrink-0 tw-w-[80px] tw-h-[80px] tw-overflow-hidden tw-bg-background">
                  {productImage ? (
                    <img
                      src={productImage}
                      alt={productTitle}
                      width="100"
                      height="100"
                      className="tw-w-full tw-h-full tw-object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className="tw-w-full tw-h-full tw-bg-gray-light"></div>
                  )}
                </div>

                {/* Product Info */}
                <div className="tw-flex-1 tw-min-h-px tw-min-w-px tw-relative">
                  <div className="tw-content-stretch tw-flex tw-flex-col tw-gap-[4px] tw-items-start tw-relative tw-w-full">
                    <div className="tw-content-stretch tw-flex tw-flex-col tw-items-start tw-relative tw-shrink-0 tw-w-full">
                      <div className="tw-flex tw-flex-col tw-font-sans tw-font-bold tw-justify-center tw-leading-tight tw-relative tw-shrink-0 tw-text-text tw-text-[16px] tw-w-full">
                        <p className="tw-leading-[24px] tw-whitespace-pre-wrap tw-m-0">{productTitle}</p>
                      </div>
                    </div>
                    <div className="tw-font-sans tw-font-normal tw-max-w-[300px] tw-relative tw-shrink-0 tw-w-full tw-flex tw-items-center tw-gap-[8px]">
                      {comparePrice > currentPrice && (
                        <span className="tw-opacity-70 tw-text-[14px] tw-line-through tw-leading-[19px]">
                          {typeof window.Shopify !== 'undefined' && window.Shopify.formatMoney
                            ? window.Shopify.formatMoney(comparePrice)
                            : `$${(comparePrice / 100).toFixed(2)}`}
                        </span>
                      )}
                      <span className="tw-text-[16px] tw-font-normal tw-leading-[22px]">
                        {typeof window.Shopify !== 'undefined' && window.Shopify.formatMoney
                          ? window.Shopify.formatMoney(currentPrice)
                          : `$${(currentPrice / 100).toFixed(2)}`}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Add to Cart Button */}
        <button
          type="button"
          id="free-gift-add-to-cart-btn"
          className="tw-bg-primary tw-content-stretch tw-flex tw-items-center tw-justify-center tw-p-[14px] tw-relative tw-rounded-[6px] tw-shrink-0 tw-w-full tw-cursor-pointer tw-border-0 tw-text-background-white tw-font-sans tw-font-bold tw-text-[16px] tw-leading-[24px] tw-uppercase disabled:tw-opacity-60 disabled:tw-cursor-not-allowed hover:tw-bg-[#b32124]"
          onClick={handleAddToCart}
          disabled={isLoading}
        >
          <span>{isLoading ? 'Adding...' : modalCta}</span>
        </button>
      </div>
    </div>
  );
};

export default FreeGiftModal;
